import styled from "@emotion/styled";

export const StyledDocContainer = styled.div`
  font-family: "Geist Mono", "Fira Code", "Cascadia Code", monospace;
  background: #f8f9fc;
  min-height: 100dvh;

  .doc-hero {
    border-bottom: 1px solid #e2e8f0;
    padding: 56px 0 40px;
    background: linear-gradient(160deg, #f0f4ff 0%, #f8f9fc 60%);
  }

  .doc-hero-inner {
    max-width: 860px;
    margin: 0 auto;
    padding: 0 32px;
  }

  .doc-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #eef0ff;
    border: 1px solid #c7d0ff;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 11px;
    color: var(--primary);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .doc-badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--primary);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  .doc-title {
    font-size: 36px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.15;
    margin-bottom: 12px;
  }

  .doc-title span {
    color: var(--primary);
  }

  .doc-subtitle {
    font-size: 15px;
    color: #64748b;
    line-height: 1.6;
    font-family: "Inter", sans-serif;
    max-width: 520px;
  }

  .doc-body {
    max-width: 860px;
    margin: 0 auto;
    padding: 48px 32px;
  }

  .doc-section {
    margin-bottom: 52px;
  }

  .doc-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }

  .doc-section-number {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: #eef0ff;
    border: 1px solid #c7d0ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: var(--primary);
    font-weight: 600;
    flex-shrink: 0;
    font-size: 18px;
  }

  .doc-section-title {
    font-size: 18px;
    font-weight: 500;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  .doc-section-desc {
    font-size: 14px;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 20px;
    font-family: "Inter", sans-serif;
    padding-left: 36px;
  }

  .doc-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 0px 12px 12px 12px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }

  .doc-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
  }

  .doc-card-lang {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .doc-card-dots {
    display: flex;
    gap: 5px;
  }

  .doc-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .doc-dot-red {
    background: #ff5f57;
  }
  .doc-dot-yellow {
    background: #febc2e;
  }
  .doc-dot-green {
    background: #28c840;
  }

  .doc-lang-label {
    font-size: 11px;
    color: #94a3b8;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .doc-copy-btn {
    background: transparent;
    border: 1px solid #c7d0ff;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 11px;
    color: var(--primary);
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .doc-copy-btn:hover {
    background: #eef0ff;
    border-color: var(--primary);
  }

  .doc-copy-btn.copied {
    color: #16a34a;
    border-color: #16a34a;
  }

  .doc-code {
    padding: 20px 22px;
    overflow-x: auto;
    font-size: 13px;
    line-height: 1.75;
    background: #fafbff;
  }

  .doc-code pre {
    margin: 0;
    white-space: pre;
  }

  .c-kw {
    color: var(--primary);
  }
  .c-fn {
    color: #2563eb;
  }
  .c-str {
    color: #16a34a;
  }
  .c-cm {
    color: #94a3b8;
    font-style: italic;
  }
  .c-var {
    color: #1e293b;
  }
  .c-tag {
    color: #ea580c;
  }
  .c-attr {
    color: #2563eb;
  }
  .c-val {
    color: #16a34a;
  }
  .c-num {
    color: #db2777;
  }

  .doc-callout {
    display: flex;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 8px;
    margin-top: 16px;
    font-size: 13px;
    line-height: 1.6;
    font-family: "Inter", sans-serif;
  }

  .doc-callout.info {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
  }

  .doc-callout.warning {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #b45309;
  }

  .doc-callout.tip {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #15803d;
  }

  .doc-callout-icon {
    font-size: 15px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .doc-callout strong {
    display: block;
    margin-bottom: 2px;
    font-weight: 500;
  }

  .doc-param-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-top: 8px;
  }

  .doc-param-table th {
    text-align: left;
    padding: 10px 14px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    font-weight: 500;
  }

  .doc-param-table td {
    padding: 11px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: top;
  }

  .doc-param-table tr:last-child td {
    border-bottom: none;
  }

  .doc-param-table tr:hover td {
    background: #fafbff;
  }

  .doc-tag-required {
    display: inline-block;
    background: #faf5ff;
    border: 1px solid #d8b4fe;
    border-radius: 4px;
    padding: 1px 7px;
    font-size: 10px;
    color: var(--primary);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 500;
  }

  .doc-tag-optional {
    display: inline-block;
    background: #f1f5f9;
    border: 1px solid #cbd5f5;
    border-radius: 4px;
    padding: 1px 7px;
    font-size: 10px;
    color: #475569;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 500;
  }

  .doc-tag-type {
    display: inline-block;
    background: #eef0ff;
    border: 1px solid #c7d0ff;
    border-radius: 4px;
    padding: 1px 7px;
    font-size: 10px;
    color: var(--primary);
    letter-spacing: 0.03em;
    font-family: "Geist Mono", monospace;
  }

  .doc-divider {
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      #e2e8f0 30%,
      #e2e8f0 70%,
      transparent
    );
    margin: 8px 0 52px;
  }

  .doc-tenant-highlight {
    display: inline;
    background: #eef0ff;
    border: 1px solid #c7d0ff;
    border-radius: 4px;
    padding: 1px 7px;
    font-size: 12px;
    color: var(--primary);
  }

  .doc-tabs {
    display: flex;
    gap: 0;
    margin-bottom: -1px;
    position: relative;
    z-index: 1;
  }

  .doc-tab {
    padding: 8px 18px;
    font-size: 12px;
    cursor: pointer;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #94a3b8;
    transition: all 0.15s ease;
    font-family: inherit;
    letter-spacing: 0.03em;
  }

  .doc-tab:first-child {
    border-radius: 8px 0 0 0;
  }

  .doc-tab:last-child {
    border-radius: 0 8px 0 0;
  }

  .doc-tab.active {
    background: #ffffff;
    color: var(--primary);
    border-bottom-color: #ffffff;
  }

  .doc-tab:hover:not(.active) {
    color: #475569;
    background: #f1f5f9;
  }
`;
